import React, { lazy, Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import '../styles/SearchPage.css';
import { Container, Stack } from 'react-bootstrap';
import usePageNav from '../hooks/usePageNav';
import getNavigationInitialState from '../initialState/navigation';
import SEO from './SEO';
import AppContext from './context/AppContext';
import { bool, object } from 'prop-types';
import useSearch from '../hooks/useSearch';
import { ToastContext } from './context/ToastContext';
import normalizeSearchParams from '../helpers/normalizeSearchParams';
import useDynamicPath from '../hooks/useDynamicPath';
import constants from '../constants.json' assert { type: 'json' };
import LoadingFallback from './LoadingFallback';
import Breadcrumbs from './Breadcrumbs';

const SearchBox = lazy(() => import('./SearchBox'));
const PageNavCurPageIndicator = lazy(() => import('./PageNavCurPageIndicator'));
const PageNav = lazy(() => import('./PageNav'));
const RelatedCollections = lazy(() => import('./RelatedCollections'));
const SearchPageProductList = lazy(() => import('./SearchPageProductList'));

export default function SearchPage({ isMobile, searchBoxRef }) {
    const { appContext } = useContext(AppContext);
    const { sendMessage } = useContext(ToastContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const normalizedSearchParams = normalizeSearchParams(searchParams);
    const { searchQuery, updateSearchParams } = useSearch();
    const [searchLoaded, setSearchLoaded] = useState(false);
    const [searchLoading, setSearchLoading] = useState(true);
    const [productList, setProductList] = useState([]);
    const navigate = useNavigate();
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    /* End search functionality */
    /* Pagination code */
    const containerRef = useRef(null);
    const [pagesData, setPagesData] = useState(getNavigationInitialState());
    const handlePageChange = (newCurPage) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('page', parseInt(newCurPage));
        setSearchParams(newSearchParams);
    };
    const {
        curPage,
        curRange,
        nextRange,
        generatedPagesData,
        paginateUser,
    } = usePageNav({
        containerRef,
        pagesData,
        initialCurPage: Math.min(parseInt(searchQuery.page || 0, 0)),
        onUpdate: handlePageChange,
    });
    /* End pagination code */

    const searchPageRef = useCallback(() => {
        searchBoxRef.current?.enableAlterPath();
    }, []);

    const fetchCategoryAndRedirect = useCallback(async (id) => {
        try {
            const url = new URL(`/api/collections/${id}`, process.env.APP_URL);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            });
            const { collection, error } = await response.json();

            if (error) {
                sendMessage(error, 'danger');
            }

            if (collection) {
                navigate(`${collectionPath[0].pathBeforeParams}/${collection.handle}`);
            }
        } catch (e) {
            sendMessage(e.message, 'danger');
        }
    }, []);

    useEffect(() => {
        const categoryId = normalizedSearchParams.get('cat');

        if (categoryId) {
            fetchCategoryAndRedirect(categoryId);
        }
    }, [normalizedSearchParams]);

    return (<Container
        ref={searchPageRef}
        fluid>
        <SEO
            seoTitle={'Search results - ' + appContext.config.storeName}
        />
        <div
            className={'section'}
        >
            <div
                style={{ display: 'none' }}
            >
                <Suspense
                    fallback={<LoadingFallback />}
                >
                    <SearchBox
                        isMobile={isMobile}
                        isPage={true}
                        searchLoaded={searchLoaded}
                        setSearchLoaded={setSearchLoaded}
                        searchLoading={searchLoading}
                        setSearchLoading={setSearchLoading}
                        searchQuery={searchQuery}
                        setSearchQuery={updateSearchParams}
                        productList={productList}
                        setProductList={setProductList}
                        setPagesData={setPagesData} />
                </Suspense>
            </div>
            <Breadcrumbs
                breadcrumbList={[
                    [
                        `Search for "${searchQuery.q ?? ''}"`,
                        `/search.php?q=${searchQuery.q}`,
                    ],
                ]}
            />
            <div
                className={'search-page--inner'}>
                {!searchLoading && <div
                    className={'my-2'}
                >
                    <span>{pagesData.count === undefined ? 0 : pagesData.count} matches found. Can&apos;t find your part? Click</span>{' '}
                    {/* Do not hardcode the link */}
                    <Link
                        to={'/Articles.asp?ID=355'}
                    >
                        HERE
                    </Link>{' '}
                    <span>to request expert assistance!</span>
                </div>}
                <Suspense
                    fallback={<LoadingFallback />}
                >
                    <RelatedCollections
                        searchQuery={searchQuery}
                    />
                </Suspense>
                <Suspense
                >
                    <SearchPageProductList
                        ref={containerRef}
                        isMobile={isMobile}
                        productList={productList}
                        searchLoading={searchLoading}
                        searchQuery={searchQuery}
                        setProductList={setProductList}
                        totalPages={pagesData.pages}
                        curPage={curPage}
                        paginateUser={paginateUser}
                        setSearchQuery={updateSearchParams}
                    >
                        {productList.length > 0 ? (
                            <>
                                <PageNavCurPageIndicator
                                    curRange={curRange}
                                    nextRange={nextRange}
                                    totalCount={pagesData.count}
                                    resourceType={'products'}
                                    noStyle={true}
                                />
                                <PageNav
                                    curPage={curPage}
                                    totalPages={pagesData.pages}
                                    generatedPagesData={generatedPagesData}
                                    paginateUser={paginateUser}
                                />
                            </>
                        ): null}
                    </SearchPageProductList>
                </Suspense>
            </div>
            {productList.length > 0 && (
                <Stack
                    direction={'horizontal'}
                    gap={3}
                    className={'justify-content-between align-items-center'}
                >
                    <Suspense
                        fallback={<LoadingFallback />}
                    >
                        <PageNav
                            curPage={curPage}
                            totalPages={pagesData.pages}
                            generatedPagesData={generatedPagesData}
                            paginateUser={paginateUser}
                        />
                    </Suspense>
                </Stack>
            )}
        </div>
    </Container>);
}

SearchPage.propTypes = {
    isMobile: bool,
    searchBoxRef: object,
};
